import React, { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  checkIsAllDomainsSelected,
  getAutoSelectedDomain,
  getSelectedDomains,
} from '@components/EntityDrawers/components/inputs/DomainDropdown/helpers';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import useStore from '@components/MainStage/store';
import { ISelectOption } from '@constants/entities/ui';
import useProjectId from '@hooks/useProjectId';
import { Stack, Switch, Typography } from '@mui/material';
import { TreeView } from '@mui/x-tree-view';
import { skipToken } from '@reduxjs/toolkit/query';
import { DomainNode } from '@store/services/nodes/types';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import { renderTree } from '@views/Project/components/ScopeFilterModal/ScopeFilterModal';
import { SelectHandler } from '@views/Project/components/ScopeFilterModal/TreeItem';
import { buildTree } from '@views/Project/helpers';

const DomainsPaperBody = () => {
  const projectId = useProjectId();
  const { data: projectDomains } = useGetProjectDomainsQuery(
    projectId || skipToken,
  );
  const [expanded, setExpanded] = useState<string[]>([]);

  const { getActiveNode } = useStore();

  const [wideDomain] = useMemo(() => {
    return buildTree(projectDomains ?? []);
  }, [projectDomains]);

  const activeNode = getActiveNode();
  const domainNodes = (activeNode?.data?.dto?.domainNodes ??
    []) as DomainNode[];

  const initialWide = domainNodes.find(
    ({ domain_id }) => domain_id === wideDomain.id,
  );

  const initialWideType = initialWide?.type;

  const hanleClick = (nodeId: string) => {
    if (expanded.includes(nodeId)) {
      setExpanded((ids) => ids.filter((id) => id !== nodeId));
    } else {
      setExpanded((ids) => [...ids, nodeId]);
    }
  };

  const handleSelect =
    (onChange: (value: any) => void, currentValue: any): SelectHandler =>
    ({ nodeId, checked }) => {
      if (checked) {
        const currentDomainType = domainNodes?.find(
          ({ domain_id }: DomainNode) => domain_id === nodeId,
        )?.type;

        const autoSelectedParentDomain = checked
          ? getAutoSelectedDomain(
              nodeId,
              projectDomains ?? [],
              wideDomain.children ?? [],
              domainNodes ?? [],
              currentValue ?? [],
            )
          : undefined;

        const newDomainsArray = [
          ...(currentValue ?? []),
          {
            value: nodeId,
            label:
              projectDomains?.find(({ id }) => id === nodeId)?.name ?? nodeId,
            type: currentDomainType || 'direct',
          },
          ...(autoSelectedParentDomain &&
          !currentValue.some(
            ({ value }: ISelectOption) =>
              value === autoSelectedParentDomain.value,
          )
            ? [autoSelectedParentDomain]
            : []),
        ];

        const { isAllDomainsSelected, isWideSelected } =
          checkIsAllDomainsSelected({
            projectDomains: projectDomains ?? [],

            selectedDomains: newDomainsArray,
          });

        if (isAllDomainsSelected) {
          onChange([
            ...newDomainsArray,
            ...(isWideSelected
              ? []
              : [
                  {
                    value: wideDomain.id,
                    label: wideDomain.name,
                    type: initialWideType ?? 'direct',
                  },
                ]),
          ]);
        } else {
          onChange(newDomainsArray);
        }
      } else {
        onChange(
          currentValue?.filter(
            (domain: ISelectOption) =>
              domain.value !== nodeId && domain.value !== wideDomain.id,
          ) ?? [],
        );
      }
    };

  return (
    <Stack>
      <Controller
        render={({ field: { value, onChange } }) => {
          const isWideSelected = value?.some(
            (domain: ISelectOption) => domain.value === wideDomain.id,
          );

          const selectedDomains = getSelectedDomains({
            selectedDomains: value,
            wideDomain,
          });

          return (
            <>
              <Stack
                direction="row"
                px={3}
                py={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography color="grey.900">{wideDomain.name}</Typography>

                <Switch
                  checked={isWideSelected}
                  onChange={(_, checked) => {
                    if (checked) {
                      onChange([
                        ...value.filter(
                          ({ value }: ISelectOption) =>
                            !domainNodes.some(
                              ({ domain_id }) => domain_id === value,
                            ),
                        ),
                        ...domainNodes
                          .map(({ domain_id, type }) => ({
                            value: domain_id,
                            label:
                              projectDomains?.find(({ id }) => id === domain_id)
                                ?.name ?? domain_id,
                            type: type || 'direct',
                          }))
                          .filter(({ value }) => value !== wideDomain.id),
                        {
                          value: wideDomain.id,
                          label: wideDomain.name,
                          type: initialWideType ?? 'direct',
                        },
                      ]);
                    } else if (initialWide) {
                      onChange([]);
                    } else {
                      onChange(
                        domainNodes.map(({ domain_id, type }) => ({
                          value: domain_id,
                          label:
                            projectDomains?.find(({ id }) => id === domain_id)
                              ?.name ?? domain_id,
                          type: type || 'direct',
                        })),
                      );
                    }
                  }}
                />
              </Stack>

              <TreeView
                expanded={expanded}
                disableSelection
                multiSelect
                selected={selectedDomains}
              >
                {wideDomain.children?.map((item) =>
                  renderTree(
                    item,
                    [],
                    handleSelect(onChange, value),
                    hanleClick,
                    undefined,
                    {
                      p: '8px 12px',
                    },
                  ),
                )}
              </TreeView>
            </>
          );
        }}
        name={CommonKeys.Domains}
      />
    </Stack>
  );
};

export default DomainsPaperBody;
